import { Suspense, lazy, useEffect, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import { Activity, LogOut, Moon, Settings, Sun } from "lucide-react";

import Dashboard from "@/components/Dashboard";
import { SettingsDialog } from "@/components/SettingsDialog";
import { fetchMe, type Me } from "@/lib/api";

// The room page pulls in livekit-client (large) — load it only when a room is opened.
const RoomPage = lazy(() => import("@/components/RoomPage"));
const Diagnostics = lazy(() => import("@/components/Diagnostics"));

/**
 * App shell — a shared header (brand + nav + identity + theme toggle) over routed pages:
 *   /            the topics dashboard
 *   /room/:roomId a live (test) meeting room
 *   /diagnostics  token + STT spend rollups
 *
 * When Google sign-in is configured server-side (config.auth), an unauthenticated visitor
 * sees a sign-in gate; otherwise the app is open/guest (unchanged behavior).
 */
type Theme = "dark" | "light";

function getInitialTheme(): Theme {
  return (document.documentElement.dataset.theme as Theme) || "dark";
}

/** Google "G" mark. */
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 1 0 44 24c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.5 5A20 20 0 0 0 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2C41.4 36.3 44 30.7 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function SignInGate() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          CodeTogether <span className="text-brand-600">Meetings</span>
        </h1>
        <p className="mt-2 text-sm text-muted">
          Sign in with your CodeTogether account to join meetings.
        </p>
      </div>
      <a
        href="/auth/google"
        className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:border-brand-600"
      >
        <GoogleIcon />
        Sign in with Google
      </a>
    </div>
  );
}

function UserChip({ me }: { me: Me }) {
  const label = me.name || me.email || "";
  const initial = label.trim()[0]?.toUpperCase() ?? "?";
  return (
    <div className="flex items-center gap-2">
      {me.picture ? (
        <img src={me.picture} alt="" className="size-6 rounded-full" />
      ) : (
        <span className="inline-flex size-6 items-center justify-center rounded-full bg-brand-600/15 text-[10px] font-semibold text-brand-600">
          {initial}
        </span>
      )}
      <span className="hidden max-w-[12rem] truncate text-xs text-muted sm:inline">{label}</span>
      <form method="POST" action="/auth/logout">
        <button
          type="submit"
          title="Sign out"
          aria-label="Sign out"
          className="rounded-md p-1.5 text-muted transition-colors hover:text-text"
        >
          <LogOut size={14} />
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [me, setMe] = useState<Me | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("portal-theme", theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    const ac = new AbortController();
    fetchMe(ac.signal)
      .then(setMe)
      // On failure, fail open (treat as auth-disabled) so a transient error can't lock the app.
      .catch(() => setMe({ authEnabled: false, authenticated: false }));
    return () => ac.abort();
  }, []);

  // Wait for the identity check before deciding whether to gate (avoids a sign-in flash).
  if (!me) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted">Loading…</div>;
  }

  if (me.authEnabled && !me.authenticated) {
    return <SignInGate />;
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex h-12 items-center justify-between border-b border-border px-6">
        <Link to="/" className="font-semibold tracking-tight">
          CodeTogether <span className="text-brand-600">Meetings</span>
        </Link>
        <div className="flex items-center gap-1">
          {me.authenticated ? <UserChip me={me} /> : null}
          {me.authenticated ? (
            <button
              aria-label="Settings"
              title="Settings"
              onClick={() => setSettingsOpen(true)}
              className="rounded-md p-2 text-muted transition-colors hover:text-text"
            >
              <Settings size={16} />
            </button>
          ) : null}
          <Link
            to="/diagnostics"
            aria-label="Diagnostics"
            title="Diagnostics"
            className="rounded-md p-2 text-muted transition-colors hover:text-text"
          >
            <Activity size={16} />
          </Link>
          <button
            aria-label="Toggle theme"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            className="rounded-md p-2 text-muted transition-colors hover:text-text"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      <main className="flex-1 px-6 py-8">
        <Suspense
          fallback={
            <div className="py-10 text-center text-sm text-muted">Loading…</div>
          }
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/room/:roomId" element={<RoomPage />} />
            <Route path="/diagnostics" element={<Diagnostics />} />
          </Routes>
        </Suspense>
      </main>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
